import {useContext} from 'react'

import Context from '@s-ui/react-context'

const getLiteralFrom =
  ({config = [], i18n}) =>
  selectedValue => {
    const selectedItem = config.find(({id}) => String(id) === String(selectedValue))

    return selectedItem && i18n.t(selectedItem.label)
  }

const toHours = minutes => {
  if (typeof minutes !== 'number' || isNaN(minutes)) return undefined
  return Math.floor(minutes / 60)
}

const getRangeTags = ({field, defaultValue, from, to, fromLabel, toLabel, rangeLabel}) =>
  rangeLabel
    ? [{field, label: rangeLabel, resetValue: defaultValue}]
    : [
        {field, label: fromLabel, resetValue: {...defaultValue, to}},
        {field, label: toLabel, resetValue: {...defaultValue, from}}
      ]

const useSearchTags = ({search = {}, pageName} = {}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const APP_NAME = config.get('APP_NAME')
  const APP_NAME_MOTOS = config.get('APP_NAME_MOTOS')
  const {
    FILTER_NAMES,
    DEFAULT_SEARCH,
    CATEGORY2_MAP,
    specificFueltypes,
    fuelTypes,
    bodyTypes: bodyTypesConfig,
    colors: colorsConfig,
    drivenWheels: drivenWheelsConfig,
    environmentalLabels: environmentalLabelsConfig,
    kwFromHp = {},
    luggageCapacityTypes: luggageCapacityConfig = [],
    offerTypes: offerTypesConfig,
    transmissionTypes: transmissionTypesConfig,
    vehicleStates: vehicleStatesConfig
  } = config.get('search')

  const getTagNamesVehicles = () => {
    let tagNames

    switch (true) {
      case pageName === 'ads-list-vans-and-commercial':
      case pageName === 'ads-list-classic-and-competition':
      case pageName === 'ads-list-autocaravans-and-trailers':
        tagNames = FILTER_NAMES.VERSION
        break
      case pageName === 'ads-list-cars-without-license':
      case pageName === 'ads-list' && APP_NAME === APP_NAME_MOTOS:
      case pageName === 'ads-list-quads':
      case pageName === 'ads-list-buggies':
      case pageName === 'ads-list-new':
      case pageName === 'ads-list-quads-new':
      case pageName === 'ads-list-buggies-new':
        tagNames = FILTER_NAMES.MAKE
        break
      default:
        tagNames = FILTER_NAMES.VEHICLES
    }

    return tagNames
  }

  const {DEFAULT_FILTERS_SEARCH, CATEGORIES_WITH_ELECTRIC_FEATURES = []} = config.get('searchFilters')

  const provincesConfig = config.get('provinces')
  const sellerTypesConfig = config.get('sellerTypes')
  const fuelTypesConfig = specificFueltypes || fuelTypes

  const _getProvinceLiteral = getLiteralFrom({i18n, config: provincesConfig})
  const _getFuelTypeLiterals = getLiteralFrom({i18n, config: fuelTypesConfig})
  const _getColorLiterals = getLiteralFrom({i18n, config: colorsConfig})
  const _getBodyTypesLiterals = getLiteralFrom({i18n, config: bodyTypesConfig})
  const _getDrivenWheelsLiterals = getLiteralFrom({
    i18n,
    config: drivenWheelsConfig
  })
  const _getTransmissionTypeLiteral = getLiteralFrom({
    i18n,
    config: transmissionTypesConfig
  })
  const _getSellerTypeLiteral = getLiteralFrom({
    i18n,
    config: sellerTypesConfig
  })
  const _getOfferTypeLiteral = getLiteralFrom({i18n, config: offerTypesConfig})
  const _getLuggageCapacityLiteral = luggageCapacityValue => {
    const selectedluggageCapacity = luggageCapacityConfig.find(
      option => JSON.stringify(option.value) === JSON.stringify(luggageCapacityValue)
    )

    return (
      luggageCapacityValue &&
      selectedluggageCapacity &&
      `${i18n.t('SEARCH_BOX_SELECT_LUGGAGE_CAPACITY_TYPE')} ${i18n.t(selectedluggageCapacity.label)}`
    )
  }

  const _getVehicleStateLiteral = getLiteralFrom({
    i18n,
    config: vehicleStatesConfig
  })

  const {
    batteryCapacity: {from: batteryCapacityFrom, to: batteryCapacityTo} = {},
    bodyTypeIds = [],
    category2Id,
    categoryId,
    chargingTimeFastMode: {from: chargingTimeFastModeFrom, to: chargingTimeFastModeTo} = {},
    chargingTimeStandardMode: {from: chargingTimeStandardModeFrom, to: chargingTimeStandardModeTo} = {},
    colorIds = [],
    commitmentMonths = [],
    doors: {from: doorsFrom, to: doorsTo} = {},
    drivenWheelsIds = [],
    environmentalLabels = [],
    equipments = [],
    electricAutonomy: {from: electricAutonomyFrom, to: electricAutonomyTo} = {},
    filteredOfferTypeId,
    financedPrice,
    fuelTypeIds = [],
    hasInstalment,
    hasOnlineFinancing,
    hasPhoto,
    hasPriceDrop,
    hasReservation,
    hasStock,
    hasWarranty,
    hp: {from: hpFrom, to: hpTo} = {},
    isCertified,
    instalment: {from: instalmentFrom, to: instalmentTo} = {},
    kms: {from: kmFrom, to: kmTo} = {},
    litres: {from: litresFrom, to: litresTo} = {},
    location: {radio: locationRadio, text: locationText} = {},
    luggageCapacity,
    entry,
    maxTerms,
    monthlySubscriptionFee: {from: monthlySubscriptionFeeFrom, to: monthlySubscriptionFeeTo} = {},
    onlyPeninsula,
    price: {from: priceFrom, to: priceTo} = {},
    priceRank = [],
    provinceIds = [],
    rating: {from: ratingFrom, to: ratingTo} = {},
    searchText,
    seats: {from: seatsFrom, to: seatsTo} = {},
    sellerTypeId,
    transmissionTypeId,
    subscriptionVehicleState,
    excludedVehicles = [],
    vehicles = [],
    year: {from: yearFrom, to: yearTo} = {}
  } = search

  const category2Items = CATEGORY2_MAP[categoryId]
  const selectedCategory2 = category2Items && category2Items.find(({id}) => Boolean(id) && id === category2Id)
  const category2Literal = selectedCategory2 && i18n.t(selectedCategory2.label)

  const prefixPriceLiteral = financedPrice ? i18n.t('COMMON.FINANCED') : i18n.t('COMMON.CASH')

  const hasPriceFrom = priceFrom && Number(priceFrom) > 0
  const hasPriceTo = priceTo && Number(priceTo) > 0

  const hasRatingFrom = ratingFrom && (Number(ratingFrom) > 0 || Number(ratingTo) > 0)
  // TODO: Move literal when the experiment is finished and UX defines the label
  const ratingLiteral = hasRatingFrom && 'Valoración +4'

  const priceFromLiteral = hasPriceFrom && `${prefixPriceLiteral} ${i18n.t('COMMON_FROM')} ${i18n.c(Number(priceFrom))}`
  const priceToLiteral = hasPriceTo && `${prefixPriceLiteral} ${i18n.t('COMMON_TO')} ${i18n.c(Number(priceTo))}`
  const priceRangeLiteral =
    hasPriceFrom &&
    hasPriceTo &&
    i18n.t(financedPrice ? 'SEARCH.TAG_RANGE_FINANCED_PRICE' : 'SEARCH.TAG_RANGE_CASH_PRICE', {
      from: i18n.c(Number(priceFrom)),
      to: i18n.c(Number(priceTo))
    })

  const hasInstalmentFrom = hasInstalment && Number(instalmentFrom) > 1
  const hasInstalmentTo = hasInstalment && Number(instalmentTo) > 0

  const hasInstalmentLiteral = hasInstalment && i18n.t('COMMON.FEE_MONTHLY')
  const instalmentFromLiteral =
    hasInstalmentFrom && `${i18n.t('COMMON.FEE')} ${i18n.t('COMMON_FROM')} ${i18n.c(Number(instalmentFrom))}`
  const instalmentToLiteral =
    hasInstalmentTo && `${i18n.t('COMMON.FEE')} ${i18n.t('COMMON_TO')} ${i18n.c(Number(instalmentTo))}`
  const instalmentRangeLiteral =
    hasInstalmentFrom &&
    hasInstalmentTo &&
    i18n.t('SEARCH.TAG_RANGE_MONTHLY_FEE', {from: i18n.c(Number(instalmentFrom)), to: i18n.c(Number(instalmentTo))})

  const hasMonthlySubscriptionFeeFrom = monthlySubscriptionFeeFrom && Number(monthlySubscriptionFeeFrom) > 0
  const hasMonthlySubscriptionFeeTo = monthlySubscriptionFeeTo && Number(monthlySubscriptionFeeTo) > 0

  const monthlySubscriptionFeeFromLiteral =
    hasMonthlySubscriptionFeeFrom && `${i18n.t('COMMON.FEE_FROM')} ${i18n.c(Number(monthlySubscriptionFeeFrom))}/mes`
  const monthlySubscriptionFeeToLiteral =
    hasMonthlySubscriptionFeeTo && `${i18n.t('COMMON.FEE_TO')} ${i18n.c(Number(monthlySubscriptionFeeTo))}/mes`
  const monthlySubscriptionFeeRangeLiteral =
    hasMonthlySubscriptionFeeFrom &&
    hasMonthlySubscriptionFeeTo &&
    i18n.t('SEARCH.TAG_RANGE_FEE', {
      from: i18n.c(Number(monthlySubscriptionFeeFrom)),
      to: `${i18n.c(Number(monthlySubscriptionFeeTo))}/mes`
    })

  const hasWarrantyLiteral = hasWarranty && i18n.t('CARD_AD_GENERIC_WARRANTY_INCLUDED')

  const yearFromLiteral = yearFrom && `${i18n.t('COMMON_FROM')} ${yearFrom}`
  const yearToLiteral = yearTo && `${i18n.t('COMMON_TO')} ${yearTo}`
  const yearRangeLiteral = yearFrom && yearTo && i18n.t('SEARCH.TAG_RANGE_BETWEEN', {from: yearFrom, to: yearTo})
  const kmFromLiteral = kmFrom && `${i18n.t('COMMON_FROM')} ${i18n.n(Number(kmFrom))} km`
  const maxEntryLiteral =
    hasInstalment && Boolean(entry) && !isNaN(entry) && `${i18n.t('FINANCING_INFO.ENTRY_OF')} ${i18n.c(Number(entry))}`
  const maxTermsLiteral =
    hasInstalment &&
    Boolean(maxTerms) &&
    !isNaN(maxTerms) &&
    `${i18n.t('FINANCING_INFO.TERMS_TO')} ${i18n.t('COMMON_N_MONTHS', maxTerms)}`

  const priceRankLiteral = Boolean(priceRank?.length) && i18n.t('COMMON.GOOD_PRICES')

  /**
   * @author @hernan.pintos
   * @description Experiment: Add 0km option to the "km to" filter
   * but the value sent is 199, which is the value that represents 0km,
   * so we need to change the literal to show 0 km
   */
  let kmToLiteral = kmTo && `${i18n.t('COMMON_TO')} ${i18n.n(Number(kmTo))} km`
  if (kmTo === 199) {
    kmToLiteral = '0 km'
  }
  const kmRangeLiteral =
    kmFrom &&
    kmTo &&
    kmTo !== 199 &&
    i18n.t('SEARCH.TAG_RANGE', {from: `${i18n.n(Number(kmFrom))} km`, to: `${i18n.n(Number(kmTo))} km`})
  const hpFromLiteral = hpFrom && `${i18n.t('COMMON_FROM')} ${hpFrom} CV`
  const hpToLiteral = hpTo && `${i18n.t('COMMON_TO')} ${hpTo} CV`
  const hasElectricFeatures = CATEGORIES_WITH_ELECTRIC_FEATURES.includes(categoryId)
  // Same hp/kW label the hp filter shows, so chip and filter agree
  const getHpLabel = hp => {
    const hpLabel = `${i18n.n(Number(hp))} ${i18n.t('COMMON.UNIT_HP')}`
    if (!hasElectricFeatures) return hpLabel
    const kw = kwFromHp[hp] || Math.round(hp / 1.341)
    return `${hpLabel} (${i18n.n(kw)} ${i18n.t('COMMON.UNIT_KW')})`
  }
  const hpRangeLiteral = hpFrom && hpTo && i18n.t('SEARCH.TAG_RANGE', {from: getHpLabel(hpFrom), to: getHpLabel(hpTo)})
  const litresFromLiteral = litresFrom && `${i18n.t('COMMON_FROM')} ${litresFrom} CC`
  const litresToLiteral = litresTo && `${i18n.t('COMMON_TO')} ${litresTo} CC`
  const litresRangeLiteral =
    litresFrom &&
    litresTo &&
    i18n.t('SEARCH.TAG_RANGE', {
      from: `${i18n.n(Number(litresFrom))} ${i18n.t('COMMON.UNIT_CC')}`,
      to: `${i18n.n(Number(litresTo))} ${i18n.t('COMMON.UNIT_CC')}`
    })

  const hasSeatsFrom = Boolean(seatsFrom)
  const hasSeatsTo = Boolean(seatsTo)

  const seatsFromLiteral = hasSeatsFrom && `${i18n.t('COMMON_FROM')} ${seatsFrom} ${i18n.t('COMMON.SEATS')}`

  const seatsToLiteral = hasSeatsTo && `${i18n.t('COMMON_TO')} ${seatsTo} ${i18n.t('COMMON.SEATS')}`
  const seatsRangeLiteral =
    hasSeatsFrom && hasSeatsTo && i18n.t('SEARCH.TAG_RANGE_SEATS', {from: seatsFrom, to: seatsTo})

  const hasDoorFrom = Boolean(doorsFrom)
  const hasDoorsTo = Boolean(doorsTo)

  const doorsFromLiteral = hasDoorFrom && `${i18n.t('COMMON_FROM')} ${doorsFrom} ${i18n.t('COMMON.DOORS')}`

  const doorsToLiteral = hasDoorsTo && `${i18n.t('COMMON_TO')} ${doorsTo} ${i18n.t('COMMON.DOORS')}`
  const doorsRangeLiteral =
    hasDoorFrom && hasDoorsTo && i18n.t('SEARCH.TAG_RANGE_DOORS', {from: doorsFrom, to: doorsTo})

  const onlyPeninsulaLiteral = onlyPeninsula && i18n.t('FORM.ONLY_PENINSULA.TITLE')

  const hasPhotoLiteral = hasPhoto && i18n.t('FORM.ONLY_WITH_PHOTO')

  const hasStockLiteral = hasStock && i18n.t('COMMON.HAS_STOCK')

  const provinceTags = provinceIds.map(provinceId => {
    const resetValue = provinceIds.filter(id => id !== provinceId)
    return {
      field: FILTER_NAMES.PROVINCE,
      label: _getProvinceLiteral(provinceId),
      resetValue
    }
  })
  const fuelTypeTags = fuelTypeIds.map(fuelTypeId => {
    const resetValue = fuelTypeIds.filter(id => id !== fuelTypeId)
    return {
      field: FILTER_NAMES.FUEL_TYPE,
      label: _getFuelTypeLiterals(fuelTypeId),
      resetValue
    }
  })

  const equipmentsTags = equipments.map(equipment => {
    const resetValue = equipments.filter(id => id !== equipment)
    return {
      field: FILTER_NAMES.EQUIPMENTS,
      label: equipment,
      resetValue
    }
  })

  const luggageCapacityLiteral = _getLuggageCapacityLiteral(luggageCapacity)
  const colorTags = colorIds.map(colorId => {
    const resetValue = colorIds.filter(id => id !== colorId)
    return {
      field: FILTER_NAMES.COLOR,
      label: _getColorLiterals(colorId),
      resetValue
    }
  })

  const bodyTypesTags = bodyTypeIds.map(bodyTypeId => {
    const resetValue = bodyTypeIds.filter(id => id !== bodyTypeId)
    const bodyTypeName =
      APP_NAME === APP_NAME_MOTOS && (pageName === 'ads-list' || pageName === 'ads-list-new')
        ? FILTER_NAMES.BODY_TYPE_CATEGORY
        : FILTER_NAMES.BODY_TYPE
    return {
      field: bodyTypeName,
      label: _getBodyTypesLiterals(bodyTypeId),
      resetValue
    }
  })

  const commitmentMonthsTags = commitmentMonths.map(stringMonths => {
    const months = Number(stringMonths)
    const resetValue = commitmentMonths.filter(id => id !== months)
    return {
      field: FILTER_NAMES.MONTHLY_SUBSCRIPTION_COMMITMENT,
      label:
        months === 0
          ? `${i18n.t('COMMON.COMMITMENT_NONE')}`
          : `${i18n.t('COMMON.COMMITMENT')} ${i18n.t('COMMON_N_MONTHS', months)}`,
      resetValue
    }
  })

  const drivenWheelsTags = drivenWheelsIds.map(drivenWheelId => {
    const resetValue = drivenWheelsIds.filter(id => id !== drivenWheelId)
    return {
      field: FILTER_NAMES.DRIVEN_WHEELS,
      label: _getDrivenWheelsLiterals(drivenWheelId),
      resetValue
    }
  })

  const transmissionTypeLiteral = _getTransmissionTypeLiteral(transmissionTypeId)
  const sellerTypeLiteral = _getSellerTypeLiteral(sellerTypeId)
  const isCertifiedLiteral = isCertified && i18n.t('COMMON_CERTIFIED')
  const offerTypeLiteral = _getOfferTypeLiteral(filteredOfferTypeId)

  const environmentalLabelsTags = environmentalLabels.map(environmentalLabel => {
    const resetValue = environmentalLabels.filter(id => id !== environmentalLabel)

    const selectedEnvironmentalLabel = environmentalLabelsConfig.find(label => label.id === environmentalLabel)

    const environmentalLabelText = selectedEnvironmentalLabel && i18n.t(selectedEnvironmentalLabel.label)

    return {
      field: FILTER_NAMES.ENVIRONMENTAL_LABEL,
      label: environmentalLabelText,
      resetValue
    }
  })

  const getVehiclesTags = () =>
    vehicles.map(({make, makeId, model = {}, version} = {}, index) => {
      const {name: modelName} = model || {}
      const newVehicles = vehicles.filter((_, vehicleIndex) => index !== vehicleIndex)
      const newExcludedVehicles = excludedVehicles.filter(ev => String(ev.makeId) !== String(makeId))
      return {
        field: getTagNamesVehicles(),
        label: [make, modelName, version].filter(Boolean).join(' '),
        preserveCase: true,
        resetValue: newVehicles,
        extraSearch: {[FILTER_NAMES.EXCLUDED_VEHICLES]: newExcludedVehicles}
      }
    })

  const getExcludedVehiclesTags = () =>
    excludedVehicles.map(({make, model = {}} = {}, index) => {
      const capitalizedMake = make ? make.charAt(0).toUpperCase() + make.slice(1).toLowerCase() : make
      const vehicle = [capitalizedMake, model?.name].filter(Boolean).join(' ')
      return {
        field: FILTER_NAMES.EXCLUDED_VEHICLES,
        label: vehicle && i18n.t('SEARCH.EXCLUDED_VEHICLE_TAG', {vehicle}),
        preserveCase: true,
        resetValue: excludedVehicles.filter((_, vehicleIndex) => index !== vehicleIndex)
      }
    })

  /**
   * This `tags` array order has been defined by UX
   */
  const tags = [
    {
      field: FILTER_NAMES.CATEGORY_2,
      label: category2Literal,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.CATEGORY_2]
    },
    {
      field: FILTER_NAMES.SEARCH_TEXT,
      label: searchText,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.SEARCH_TEXT]
    },
    ...getVehiclesTags(),
    ...getExcludedVehiclesTags(),
    ...getRangeTags({
      field: FILTER_NAMES.PRICE,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.PRICE],
      from: priceFrom,
      to: priceTo,
      fromLabel: priceFromLiteral,
      toLabel: priceToLiteral,
      rangeLabel: priceRangeLiteral
    }),
    {
      field: FILTER_NAMES.HAS_INSTALMENT,
      label: hasInstalmentLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.HAS_INSTALMENT]
    },
    ...getRangeTags({
      field: FILTER_NAMES.INSTALMENT,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.INSTALMENT],
      from: instalmentFrom || null,
      to: instalmentTo || null,
      fromLabel: instalmentFromLiteral,
      toLabel: instalmentToLiteral,
      rangeLabel: instalmentRangeLiteral
    }),
    ...getRangeTags({
      field: FILTER_NAMES.MONTHLY_SUBSCRIPTION_FEE,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.MONTHLY_SUBSCRIPTION_FEE],
      from: monthlySubscriptionFeeFrom,
      to: monthlySubscriptionFeeTo,
      fromLabel: monthlySubscriptionFeeFromLiteral,
      toLabel: monthlySubscriptionFeeToLiteral,
      rangeLabel: monthlySubscriptionFeeRangeLiteral
    }),
    {
      field: FILTER_NAMES.PRICE_RANK,
      label: priceRankLiteral,
      resetValue: DEFAULT_FILTERS_SEARCH[FILTER_NAMES.PRICE_RANK]
    },
    ...commitmentMonthsTags,
    {
      field: FILTER_NAMES.HAS_WARRANTY,
      label: hasWarrantyLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.HAS_WARRANTY]
    },
    {
      field: FILTER_NAMES.RATING,
      label: ratingLiteral,
      resetValue: DEFAULT_FILTERS_SEARCH[FILTER_NAMES.RATING]
    },
    ...getRangeTags({
      field: FILTER_NAMES.YEAR,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.YEAR],
      from: yearFrom,
      to: yearTo,
      fromLabel: yearFromLiteral,
      toLabel: yearToLiteral,
      rangeLabel: yearRangeLiteral
    }),
    ...getRangeTags({
      field: FILTER_NAMES.KMS,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.KMS],
      from: kmFrom,
      to: kmTo,
      fromLabel: kmFromLiteral,
      toLabel: kmToLiteral,
      rangeLabel: kmRangeLiteral
    }),
    ...provinceTags,
    {
      field: FILTER_NAMES.LOCATION,
      label: locationRadio ? `En ${locationRadio} kms de ${locationText}` : locationText,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.LOCATION]
    },
    {
      field: FILTER_NAMES.ONLY_PENINSULA,
      label: onlyPeninsulaLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.ONLY_PENINSULA]
    },
    ...fuelTypeTags,
    ...getRangeTags({
      field: FILTER_NAMES.ELECTRIC_BATTERY_CAPACITY,
      defaultValue: DEFAULT_FILTERS_SEARCH[FILTER_NAMES.ELECTRIC_BATTERY_CAPACITY],
      from: batteryCapacityFrom,
      to: batteryCapacityTo,
      fromLabel: batteryCapacityFrom && `${i18n.t('COMMON_FROM')} ${batteryCapacityFrom} ${i18n.t('COMMON.UNIT_KWH')}`,
      toLabel: batteryCapacityTo && `${i18n.t('COMMON_TO')} ${batteryCapacityTo} ${i18n.t('COMMON.UNIT_KWH')}`,
      rangeLabel:
        batteryCapacityFrom &&
        batteryCapacityTo &&
        i18n.t('SEARCH.TAG_RANGE', {
          from: `${batteryCapacityFrom} ${i18n.t('COMMON.UNIT_KWH')}`,
          to: `${batteryCapacityTo} ${i18n.t('COMMON.UNIT_KWH')}`
        })
    }),
    ...getRangeTags({
      field: FILTER_NAMES.ELECTRIC_AUTONOMY,
      defaultValue: DEFAULT_FILTERS_SEARCH[FILTER_NAMES.ELECTRIC_AUTONOMY],
      from: electricAutonomyFrom,
      to: electricAutonomyTo,
      fromLabel:
        electricAutonomyFrom && `${i18n.t('COMMON_FROM')} ${electricAutonomyFrom} ${i18n.t('COMMON.UNIT_KMS')}`,
      toLabel: electricAutonomyTo && `${i18n.t('COMMON_TO')} ${electricAutonomyTo} ${i18n.t('COMMON.UNIT_KMS')}`,
      rangeLabel:
        electricAutonomyFrom &&
        electricAutonomyTo &&
        i18n.t('SEARCH.TAG_RANGE', {
          from: `${electricAutonomyFrom} ${i18n.t('COMMON.UNIT_KMS')}`,
          to: `${electricAutonomyTo} ${i18n.t('COMMON.UNIT_KMS')}`
        })
    }),
    ...getRangeTags({
      field: FILTER_NAMES.ELECTRIC_CHARGING_TIME_FAST_MODE,
      defaultValue: DEFAULT_FILTERS_SEARCH[FILTER_NAMES.ELECTRIC_CHARGING_TIME_FAST_MODE],
      from: chargingTimeFastModeFrom,
      to: chargingTimeFastModeTo,
      fromLabel:
        chargingTimeFastModeFrom &&
        `${i18n.t('COMMON_FROM')} ${chargingTimeFastModeFrom} ${i18n.t('COMMON.UNIT_MINUTES')}`,
      toLabel:
        chargingTimeFastModeTo && `${i18n.t('COMMON_TO')} ${chargingTimeFastModeTo} ${i18n.t('COMMON.UNIT_MINUTES')}`,
      rangeLabel:
        chargingTimeFastModeFrom &&
        chargingTimeFastModeTo &&
        i18n.t('SEARCH.TAG_RANGE', {
          from: `${chargingTimeFastModeFrom} ${i18n.t('COMMON.UNIT_MINUTES')}`,
          to: `${chargingTimeFastModeTo} ${i18n.t('COMMON.UNIT_MINUTES')}`
        })
    }),
    ...getRangeTags({
      field: FILTER_NAMES.ELECTRIC_CHARGING_TIME_STANDARD_MODE,
      defaultValue: DEFAULT_FILTERS_SEARCH[FILTER_NAMES.ELECTRIC_CHARGING_TIME_STANDARD_MODE],
      from: chargingTimeStandardModeFrom,
      to: chargingTimeStandardModeTo,
      fromLabel:
        toHours(chargingTimeStandardModeFrom) &&
        `${i18n.t('COMMON_FROM')} ${toHours(chargingTimeStandardModeFrom)} ${i18n.t('COMMON.UNIT_HOUR')}`,
      toLabel:
        toHours(chargingTimeStandardModeTo) &&
        `${i18n.t('COMMON_TO')} ${toHours(chargingTimeStandardModeTo)} ${i18n.t('COMMON.UNIT_HOUR')}`,
      rangeLabel:
        toHours(chargingTimeStandardModeFrom) &&
        toHours(chargingTimeStandardModeTo) &&
        i18n.t('SEARCH.TAG_RANGE', {
          from: `${toHours(chargingTimeStandardModeFrom)} ${i18n.t('COMMON.UNIT_HOUR')}`,
          to: `${toHours(chargingTimeStandardModeTo)} ${i18n.t('COMMON.UNIT_HOUR')}`
        })
    }),
    ...environmentalLabelsTags,
    {
      field: FILTER_NAMES.LUGGAGE_CAPACITY,
      label: luggageCapacityLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.LUGGAGE_CAPACITY]
    },
    ...colorTags,
    ...bodyTypesTags,
    ...drivenWheelsTags,
    ...getRangeTags({
      field: FILTER_NAMES.DOOR,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.DOOR],
      from: doorsFrom,
      to: doorsTo,
      fromLabel: doorsFromLiteral,
      toLabel: doorsToLiteral,
      rangeLabel: doorsRangeLiteral
    }),
    {
      field: FILTER_NAMES.TRANSMISSION_TYPE,
      label: transmissionTypeLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.TRANSMISSION_TYPE]
    },
    ...getRangeTags({
      field: FILTER_NAMES.HP,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.HP],
      from: hpFrom,
      to: hpTo,
      fromLabel: hpFromLiteral,
      toLabel: hpToLiteral,
      rangeLabel: hpRangeLiteral
    }),
    ...getRangeTags({
      field: FILTER_NAMES.LITRES,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.LITRES],
      from: litresFrom,
      to: litresTo,
      fromLabel: litresFromLiteral,
      toLabel: litresToLiteral,
      rangeLabel: litresRangeLiteral
    }),
    ...getRangeTags({
      field: FILTER_NAMES.SEAT,
      defaultValue: DEFAULT_SEARCH[FILTER_NAMES.SEAT],
      from: seatsFrom,
      to: seatsTo,
      fromLabel: seatsFromLiteral,
      toLabel: seatsToLiteral,
      rangeLabel: seatsRangeLiteral
    }),
    ...equipmentsTags,
    {
      field: FILTER_NAMES.SELLER_TYPE,
      label: sellerTypeLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.SELLER_TYPE]
    },
    {
      field: FILTER_NAMES.IS_CERTIFIED,
      label: isCertifiedLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.IS_CERTIFIED]
    },
    {
      field: FILTER_NAMES.OFFER_TYPE,
      label: offerTypeLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.OFFER_TYPE]
    },
    {
      field: FILTER_NAMES.HAS_PHOTO,
      label: hasPhotoLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.HAS_PHOTO]
    },
    {
      field: FILTER_NAMES.HAS_STOCK,
      label: hasStockLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.HAS_STOCK]
    },
    {
      field: FILTER_NAMES.VEHICLE_STATE,
      label: _getVehicleStateLiteral(subscriptionVehicleState),
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.VEHICLE_STATE]
    },
    {
      field: FILTER_NAMES.FINANCING_ENTRY,
      label: maxEntryLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.FINANCING_ENTRY]
    },
    {
      field: FILTER_NAMES.FINANCING_TERMS,
      label: maxTermsLiteral,
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.FINANCING_TERMS]
    },
    {
      field: FILTER_NAMES.HAS_ONLINE_FINANCING,
      label: hasOnlineFinancing && i18n.t('FILTERS.HAS_ONLINE_FINANCING.TAG'),
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.HAS_ONLINE_FINANCING]
    },
    {
      field: FILTER_NAMES.HAS_RESERVATION,
      label: hasReservation && i18n.t('FILTERS.HAS_RESERVATION.LABEL'),
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.HAS_RESERVATION]
    },
    {
      field: FILTER_NAMES.HAS_PRICE_DROP,
      label: hasPriceDrop && i18n.t('FILTERS.HAS_PRICE_DROP.LABEL'),
      resetValue: DEFAULT_SEARCH[FILTER_NAMES.HAS_PRICE_DROP]
    }
  ]

  return tags.filter(({label}) => Boolean(label))
}

export default useSearchTags
